/**
 * Mitarbeiter-Status-Dialog — öffnet sich per Klick auf die Statuszelle (Wochenplan und Mitarbeiteransicht) und
 * zeigt ausführlich, was die Zelle verdichtet: die Stunden des Abrechnungsmonats (Plan/Ist samt Plan bis heute
 * und Abweichung), die Konten (Resturlaub, Gutzeit-Saldo, für Budget-Rollen AG-Kosten/h) und — nur für Rollen
 * mit Berechtigung — die aktuellen Urlaubsanträge zum Genehmigen/Ablehnen. Aufbau wie die Editor-Dialoge (.dlg,
 * siehe bedarfZuordnung.js).
 *
 * Die Kennzahlen kommen aus util/statusWerte.js — dieselbe Quelle wie die Statuszelle. Weitere Konten (z.B.
 * Kranktage aus m.Uebersicht.Kranktage) lassen sich in kontenVon() als zusätzliche Kachel ergänzen.
 */

import dayjs from '../../../../shared/services/dayjs.js';
import { convertNumberDE } from '../util.js';
import { berechneStatusWerte, formatStunden, STATUS_RESTURLAUB_KNAPP_TAGE } from '../util/statusWerte.js';
import { istAppDialogOffen } from './bestaetigung.js';

const MODAL_ID = 'ma-status-modal';
let escHandler = null;

const STATUS_TEXT = { offen: 'Offen', genehmigt: 'Genehmigt', abgelehnt: 'Abgelehnt' };

function el(tag, className, text) {
	const e = document.createElement(tag);
	if (className) e.className = className;
	if (text !== undefined) e.textContent = text;
	return e;
}

function schliesse() {
	document.getElementById(MODAL_ID)?.remove();
	if (escHandler) {
		document.removeEventListener('keydown', escHandler);
		escHandler = null;
	}
}

/** Avatar im Dialogkopf: Initialen, darüber das Profilbild (gleiche URL wie in der Mitarbeiterzelle, also aus dem Cache). */
function baueAvatar(m, bereichFarbe) {
	const av = el('div', 'ic ms-av', `${m.Personalien.Vorname?.[0] ?? ''}${m.Personalien.Nachname?.[0] ?? ''}`.toUpperCase());
	if (bereichFarbe) av.style.setProperty('--bereich-farbe', bereichFarbe);
	if (m.AvatarStand) {
		const bild = el('img');
		bild.alt = '';
		bild.addEventListener('error', () => bild.remove());
		bild.src = `/dienstplan/api/mitarbeiter/${m._id}/avatar?v=${encodeURIComponent(m.AvatarStand)}`;
		av.appendChild(bild);
	}
	return av;
}

/** Abschnitt "Stunden": Plan/Soll groß mit Abweichung, die beiden Spuren Plan/Ist und die Einzelwerte. */
function baueStunden(w) {
	const karte = el('div', 'ms-card');
	const kopf = el('div', 'ms-hd');
	kopf.append(el('span', 'ms-big', formatStunden(w.plan)), el('span', 'ms-of', w.soll > 0 ? `/ ${formatStunden(w.soll)} h Monatssoll` : 'h geplant · kein Monatssoll'));
	if (w.soll > 0) kopf.appendChild(el('span', `ms-dv ${w.abwKlasse}`, w.abwText));
	karte.appendChild(kopf);

	if (w.soll > 0) {
		const pct = (wert) => `${(wert / w.skala) * 100}%`;
		const spuren = el('div', 'ms-duo');
		const spur = (label, breite, filKlasse, wert, sollMarke) => {
			const trk = el('div', 'ms-trk');
			const fil = el('div', `fil ${filKlasse}`);
			fil.style.width = breite;
			trk.appendChild(fil);
			if (sollMarke) {
				const tgt = el('div', 'tgt');
				tgt.style.left = sollMarke;
				tgt.title = `Monatssoll ${formatStunden(w.soll)} h`;
				trk.appendChild(tgt);
			}
			spuren.append(el('span', 'l', label), trk, el('span', 'n2', `${formatStunden(wert)} h`));
		};
		spur('Plan', pct(w.plan), `${w.unterplant ? 'warn' : ''}${w.over ? ' over' : ''}`, w.plan, pct(w.soll));
		spur('Ist', pct(w.ist), `erf ${w.abwKlasse}`, w.ist);
		karte.appendChild(spuren);
	}

	const kpis = el('div', 'ms-kpis');
	const kpi = (label, wert, klasse = '') => {
		const k = el('div', `ms-kpi${klasse ? ` ${klasse}` : ''}`);
		k.append(el('span', 'k', label), el('span', 'v', wert));
		kpis.appendChild(k);
	};
	kpi('Geplant', `${formatStunden(w.plan)} h`);
	kpi('Plan bis heute', `${formatStunden(w.planBisHeute)} h`);
	kpi('Ist erfasst', `${formatStunden(w.ist)} h`);
	kpi('Abweichung', w.abwText, w.abwKlasse);
	if (w.over > 0) kpi('Über Soll', `${formatStunden(w.over)} h`, 'hi');
	karte.appendChild(kpis);
	return karte;
}

/**
 * Konten-Kacheln (Stand heute). Neue Konten hier als weiteren Eintrag ergänzen.
 * @returns {array<{icon:string, label:string, wert:string, einheit:string, klasse?:string, titel?:string}>}
 */
function kontenVon(w, zeigeAgKosten) {
	const konten = [
		{
			icon: 'bi-airplane-fill ms-flug',
			label: 'Resturlaub',
			wert: formatStunden(w.rest),
			einheit: w.anspruch > 0 ? `von ${formatStunden(w.anspruch)} Tagen` : 'Tage',
			klasse: w.rest <= STATUS_RESTURLAUB_KNAPP_TAGE ? 'warn' : '',
		},
		{
			icon: 'bi-plus-slash-minus',
			label: 'Gutzeit-Saldo',
			wert: `${w.ueb > 0 ? '+' : ''}${formatStunden(w.ueb)}`,
			einheit: 'h',
			klasse: w.ueb < 0 ? 'neg' : '',
		},
	];
	if (zeigeAgKosten) {
		konten.push({ icon: 'bi-cash-coin', label: 'AG-Kosten', wert: convertNumberDE(w.agKosten, 2), einheit: '€/h', titel: 'Geschätzte Arbeitgeberkosten pro Stunde' });
	}
	return konten;
}

function baueKonten(konten) {
	const reihe = el('div', 'ms-tiles');
	konten.forEach((konto) => {
		const kachel = el('div', `ms-tile${konto.klasse ? ` ${konto.klasse}` : ''}`);
		if (konto.titel) kachel.title = konto.titel;
		const kopf = el('div', 'k');
		kopf.innerHTML = `<i class="bi ${konto.icon}"></i>`;
		kopf.appendChild(document.createTextNode(konto.label));
		const wert = el('div', 'v', konto.wert);
		wert.appendChild(el('em', '', konto.einheit));
		kachel.append(kopf, wert);
		reihe.appendChild(kachel);
	});
	return reihe;
}

/** Eine Karte je aktuellem Urlaubsantrag mit Status-Chip und Genehmigen/Ablehnen (nur die jeweils sinnvolle Aktion). */
function baueUrlaubsantraege(antraege, onUrlaubAktion) {
	const liste = el('div', 'ms-ua-liste');
	if (antraege.length === 0) {
		liste.appendChild(el('div', 'ms-leer', 'Keine aktuellen Urlaubsanträge'));
		return liste;
	}
	antraege.forEach((antrag) => {
		const karte = el('div', 'ms-ua');
		const zeit = el('div', 'ms-ua-zeit');
		zeit.innerHTML = '<i class="bi bi-calendar3"></i>';
		zeit.appendChild(document.createTextNode(`${dayjs(antrag.UrlaubVon).tz().format('dd, DD.MM.YY')} – ${dayjs(antrag.UrlaubBis).tz().format('dd, DD.MM.YY')}`));
		const tage = el('div', 'ms-ua-tage', `${formatStunden(antrag.AnzahlEGFZTage || 0)} ${antrag.AnzahlEGFZTage === 1 ? 'Tag' : 'Tage'}`);
		const chip = el('span', `ms-chip ${antrag.UrlaubStatus}`, STATUS_TEXT[antrag.UrlaubStatus] || antrag.UrlaubStatus);
		const aktionen = el('div', 'ms-ua-akt');
		const knopf = (status, klasse, icon, text) => {
			const btn = el('button', `ms-btn ${klasse}`);
			btn.type = 'button';
			btn.innerHTML = `<i class="bi ${icon}"></i>`;
			btn.appendChild(document.createTextNode(text));
			btn.addEventListener('click', async () => {
				aktionen.querySelectorAll('button').forEach((b) => { b.disabled = true; });
				const geaendert = await onUrlaubAktion(antrag, status);
				if (geaendert) {
					schliesse();
				} else {
					aktionen.querySelectorAll('button').forEach((b) => { b.disabled = false; });
				}
			});
			aktionen.appendChild(btn);
		};
		if (antrag.UrlaubStatus !== 'genehmigt') knopf('genehmigt', 'pos', 'bi-check-lg', 'Genehmigen');
		if (antrag.UrlaubStatus !== 'abgelehnt') knopf('abgelehnt', 'neg', 'bi-x-lg', 'Ablehnen');
		karte.append(zeit, tage, chip, aktionen);
		liste.appendChild(karte);
	});
	return liste;
}

function sektion(titel, untertitel) {
	const kopf = el('div', 'zp-sec', titel);
	if (untertitel) kopf.appendChild(el('span', 'ms-sec-sub', untertitel));
	return kopf;
}

/**
 * Öffnet den Dialog.
 * @param {object} opt
 * @param {object} opt.mitarbeiter frisch geladene Person (mit Urlaubsanträgen)
 * @param {object} [opt.stunden] Monatsstunden der Person aus stundenMonat.Mitarbeiter
 * @param {object} [opt.zeitraum] stundenMonat.Zeitraum { Monat, Start, Ende }
 * @param {boolean} opt.zeigeAgKosten Budget-Rolle?
 * @param {string} [opt.bereichFarbe]
 * @param {boolean} opt.zeigeUrlaubsantraege Abschnitt Urlaubsanträge zeigen (nur Rollen mit Berechtigung)?
 * @param {function} opt.onUrlaubAktion (antrag, 'genehmigt'|'abgelehnt') => Promise<boolean> — true schließt den Dialog
 */
function oeffneMitarbeiterStatus({ mitarbeiter: m, stunden, zeitraum, zeigeAgKosten, bereichFarbe, zeigeUrlaubsantraege, onUrlaubAktion }) {
	schliesse();
	const w = berechneStatusWerte(m, stunden);
	const heute = dayjs.tz(new Date(), dayjs.tz.DEFAULT_TZ);

	const modal = el('div', 'modal');
	modal.id = MODAL_ID;
	modal.style.display = 'block';
	modal.innerHTML = `
		<div class="modal-inhalt" style="width:600px;max-height:88vh;">
			<div class="dlg" style="max-height:88vh;">
				<div class="dlg-head" id="ms-head">
					<div style="min-width:0;">
						<div class="ttl" id="ms-titel"></div>
						<div class="sub" id="ms-sub"></div>
					</div>
					<i class="bi bi-x-lg x" id="ms-close"></i>
				</div>
				<div class="dlg-body ms-body" id="ms-body"></div>
				<div class="dlg-foot">
					<div class="sp"></div>
					<button class="dbtn" id="ms-schliessen">Schließen</button>
				</div>
			</div>
		</div>`;

	// Kopf: Avatar, Name, Personalnummer · Bezugszeitraum der Stunden
	const kopf = modal.querySelector('#ms-head');
	kopf.insertBefore(baueAvatar(m, bereichFarbe), kopf.firstChild);
	modal.querySelector('#ms-titel').textContent = `${m.Personalien.Vorname} ${m.Personalien.Nachname}`;
	const sub = modal.querySelector('#ms-sub');
	const pnr = m.Personalien?.Personalnummer;
	if (pnr !== undefined && pnr !== null && pnr !== '') sub.appendChild(el('span', '', `#${pnr}`));
	// Bezugsmonat der Stunden (Abrechnungszeitraum), der genaue Zeitraum steht im Tooltip
	const monat = zeitraum ? dayjs.tz(zeitraum.Monat, dayjs.tz.DEFAULT_TZ).format('MMMM YYYY') : '';
	if (zeitraum) {
		const zr = el('span');
		zr.title = `${dayjs(zeitraum.Start).tz().format('DD.MM.YYYY')} – ${dayjs(zeitraum.Ende).tz().format('DD.MM.YYYY')}`;
		zr.innerHTML = '<i class="bi bi-calendar3"></i>';
		zr.appendChild(document.createTextNode(` ${monat}`));
		sub.appendChild(zr);
	}

	// Inhalt
	const body = modal.querySelector('#ms-body');
	body.append(
		sektion('Stunden', monat),
		baueStunden(w),
		sektion('Konten', `Stand ${heute.format('DD.MM.YYYY')}`),
		baueKonten(kontenVon(w, zeigeAgKosten)),
	);
	if (zeigeUrlaubsantraege) {
		// Aktuelle Urlaubsanträge: alle, die nicht vor dieser Woche enden (wie bisher), chronologisch
		const wochenStart = heute.startOf('week');
		const antraege = (m.Beschaeftigung[0].Urlaubsantraege ?? [])
			.filter((ua) => wochenStart.isBefore(ua.UrlaubBis))
			.sort((a, b) => new Date(a.UrlaubVon) - new Date(b.UrlaubVon));
		const offen = antraege.filter((ua) => ua.UrlaubStatus === 'offen').length;
		body.append(sektion('Urlaubsanträge', offen > 0 ? `${offen} offen` : ''), baueUrlaubsantraege(antraege, onUrlaubAktion));
	}

	document.body.appendChild(modal);
	// Schließen per Backdrop-Klick, X, Button und Escape (nicht, solange darüber eine Bestätigung offen ist)
	modal.addEventListener('mousedown', (e) => { if (e.target === modal) schliesse(); });
	modal.querySelector('#ms-close').addEventListener('click', schliesse);
	modal.querySelector('#ms-schliessen').addEventListener('click', schliesse);
	escHandler = (e) => { if (e.key === 'Escape' && !istAppDialogOffen()) schliesse(); };
	document.addEventListener('keydown', escHandler);
}

export { oeffneMitarbeiterStatus };
